<template>
    <div class="user-area-menu-wrapper" :class="{'mobile': getMobileAppState}" @click="[showUserAreaMenu = true, triedFetchingImg = false]">
        <div class="user-area-button">
            <!-- User Profile Pic -->
            <img
            id="profile-pic"
            class="user-profile-pic"
            :src="avatarBackgroundUrl"
            @error="errorImage"/>
            <!-- User Name -->
            <div class="user-name">{{getAllCustomerInfo.displayName}}</div>
            <!-- Icon -->
            <icon-arrow-down/>
        </div>
        <!-- Menu Wrapper -->
        <teleport to="#side-layer-overlay-teleport" :disabled="!getMobileAppState">
            <transition name="user-menu-area-animation" mode="out-in">
                <!-- User are menu -->
                <div
                v-if="showUserAreaMenu"
                v-click-outside="() => { !getMobileAppState ? showUserAreaMenu = false : null }"
                class="user-area-menu"
                :class="{ 'mobile': getMobileAppState, 'dark-mode': getAppThemeMode === 'dark' }">
                    <!-- Top Area -->
                    <div
                    class="top-area-wrapper"
                    :class="{
                        'mobile-status-bar-android': getDeviceInUse === 'android',
                        'mobile-status-bar-ios': getDeviceInUse === 'ios'
                    }">
                        <div v-if="getMobileAppState" class="mobile-only-components-wrapper">
                            <!-- Back Button -->
                            <button-tertiary
                            buttonStyle="round"
                            buttonSize="small"
                            icon="arrow-left-full"
                            @clicked="showUserAreaMenu = false"/>
                            <!-- Mode Selector -->
                            <dropdown-mode :options="modeDropdownOptions" :selectedOption="selectedMode" @clicked="emit('changeMode', $event)"/>
                        </div>
                        <img  id="top-are-profile-pic" class="user-profile-pic" :src="successAvatarBackgroundUrl" @error="errorImage"/>
                        <div class="left-side-wrapper">
                            <div class="user-name">{{getAllCustomerInfo.displayName}}</div>
                            <button-secondary
                            v-if="userAreaEntries && profileMenuEntry"
                            buttonSize="tiny"
                            :style="{'text-transform': 'capitalize'}"
                            icon="manage-accounts"
                            :label="profileMenuEntry.name"
                            @clicked="redirectToRoute(profileMenuEntry.link)"/>
                        </div>
                    </div>
                    <!-- Bottom Area -->
                    <div class="bottom-area-wrapper">
                        <!-- Definitions for current session -->
                        <div class="current-session-wrapper">
                            <div class="current-session">
                                <div class="acession-name">{{getAllCustomerInfo.contractName || getAllCustomerInfo.customer.username}}</div>
                                <div class="account-groups-type">{{accountGroupsType}}</div>
                            </div>
                            <button-primary
                            v-if="userAreaEntries && definitionsMenuEntry"
                            buttonSize="small"
                            icon="settings"
                            :label="definitionsMenuEntry.name"
                            @clicked="redirectToRoute(definitionsMenuEntry.link)"/>
                        </div>
                        <!-- Memorized Acessions -->
                        <div v-if="memorizedAcessions" class="memorized-acessions-wrapper">
                            <divider-generic icon="saved-login" dividerSize="medium" :title="t('menu.headerMenu.userArea.memorizedAcessionsTitle')"/>
                        </div>
                        <!-- Buttons -->
                        <div class="buttons-bottom-wrapper">
                            <button-tertiary
                            buttonSize="small"
                            :label="t('menu.headerMenu.userArea.changeLoggedAcession')"
                            @clicked="showChangeLoggedSessionModal = true"/>
                            <!-- Manage Acessions Button -->
                            <button-secondary
                            v-if="userAreaEntries && acessionManagementMenuEntry"
                            buttonSize="small"
                            icon="more-settings"
                            :label="acessionManagementMenuEntry.name"
                            @clicked="redirectToRoute(acessionManagementMenuEntry.link)"/>
                        </div>
                    </div>
                </div>
            </transition>
        </teleport>
    </div>
    <!-- Change Logged Session Modal -->
    <modal-centered-generic
    v-if="showChangeLoggedSessionModal"
    titleIcon="logout"
    :title="t('menu.headerMenu.userArea.changeLoggedAcessionModal.title')"
    :text="t('menu.headerMenu.userArea.changeLoggedAcessionModal.text')"
    :rightButtonText="t('menu.headerMenu.userArea.changeLoggedAcessionModal.leaveButton')"
    rightButtonIcon="check-line"
    @hideMe="showChangeLoggedSessionModal = false"
    @rightButtonClicked="logoutHandler"/>
    <!-- Loader Generic -->
    <loaders-generic v-if="showOverlayLoader" isOverlay/>
</template>

<script setup>
import IconArrowDown from '@/assets/icons/arrow-down.svg?inline'
import ButtonSecondary from '@/components/generic/buttons/Secondary'
import ButtonPrimary from '@/components/generic/buttons/Primary'
import DividerGeneric from '@/components/generic/dividers/Generic'
import ButtonTertiary from '@/components/generic/buttons/Tertiary'
import DropdownMode from '@/components/generic/dropdowns/mode/Mode'
import ModalCenteredGeneric from '@/components/generic/modals/centered/Generic'
import LoadersGeneric from '@/components/generic/loaders/Generic'
/* Vue & Assets */
import { onMounted, ref, computed, watch } from 'vue'
import { navigateByPath } from '@/router/helpers/navigation'
import { requestCustomerImageUrl } from '@/network/api/customers/customers.api.js'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['changeMode'])

/* Props */
const props = defineProps({
	modeDropdownOptions: Array,
    selectedMode: String
})

/* mapActions & mapGetters */
const { logout, forceLogout } = mapActions(moduleMapper.SESSION)
const { getAllCustomerInfo } = mapGetters(moduleMapper.SESSION)
const { getMenu } = mapGetters(moduleMapper.MENU)
const { getMobileAppState, getAppThemeMode, getDeviceInUse } = mapGetters()

const showUserAreaMenu = ref(false)
const showChangeLoggedSessionModal = ref(false)
const showOverlayLoader = ref(false)
const showingPlaceholderImage = ref(false)
const triedFetchingImg = ref(false)

const defaultImage = computed(() => {
    return getAppThemeMode.value === 'dark' ? './images/user_image_placeholder_dark.svg' : './images/user_image_placeholder.svg'
})

const avatarBackgroundUrl = computed(() => {
    return getAllCustomerInfo.value ? getAllCustomerInfo.value.customer.avatarImageUrl : defaultImage.value
})

const successAvatarBackgroundUrl = ref(avatarBackgroundUrl.value)

watch(() => avatarBackgroundUrl.value, (newVal, oldVal) => {
    triedFetchingImg.value = false
    showingPlaceholderImage.value = false
    successAvatarBackgroundUrl.value = avatarBackgroundUrl.value
})

const errorImage = event => {
    if (!triedFetchingImg.value) {
        triedFetchingImg.value = true
        if (process.env.VUE_APP_ENV === 'QUA') {
            event.target.src = requestCustomerImageUrl()
            successAvatarBackgroundUrl.value = requestCustomerImageUrl()
            return
        }
    }

    showingPlaceholderImage.value = true
    event.target.src = getAppThemeMode.value === 'dark' ? './images/user_image_placeholder_dark.svg' : './images/user_image_placeholder.svg'
    successAvatarBackgroundUrl.value = event.target.src
}

const accountGroupsType = computed(() => {
    if (getAllCustomerInfo.value.accountGroups.length === 1) return t('menu.headerMenu.userArea.accountGroupSimple')
    return t('menu.headerMenu.userArea.accountGroupMultiple')
})

const userAreaEntries = computed(() => {
	return getMenu.value.filter(obj => obj.additionalData?.menuType === 'HEADER').find(obj => obj.label === 'userArea')
})

const profileMenuEntry = computed(() => {
    return userAreaEntries.value.children.find(obj => obj.label === 'userArea_profile')
})

const definitionsMenuEntry = computed(() => {
    return userAreaEntries.value.children.find(obj => obj.label === 'userArea_definitions')
})

const acessionManagementMenuEntry = computed(() => {
    return userAreaEntries.value.children.find(obj => obj.label === 'userArea_acessionManagement')
})

const redirectToRoute = route => {
    navigateByPath(route)
    if (!getMobileAppState.value) showUserAreaMenu.value = false
}

const logoutHandler = async () => {
    showChangeLoggedSessionModal.value = false
    showOverlayLoader.value = true

    try {
        await logout()
    } finally {
        forceLogout()
    }
}

watch(() => getAppThemeMode.value, (newVal, oldVal) => {
    if (!showingPlaceholderImage.value) return
    const newImage = getAppThemeMode.value === 'dark' ? './images/user_image_placeholder_dark.svg' : './images/user_image_placeholder.svg'
    document.getElementById('profile-pic').src = newImage
    if (showUserAreaMenu.value && getMobileAppState.value) document.getElementById('top-are-profile-pic').src = newImage
})

onMounted(() => {
    triedFetchingImg.value = false
})
</script>

<style lang="scss" scoped>
.user-area-menu-wrapper {
    max-width: 182px;
    width: 100%;
    min-width: 40px;

    display: flex;
    align-items: center;

    border-radius: 24px;
    background-color: var(--secondary-light-2);
    cursor: pointer;

    &:not(.mobile) {
        position: relative;
    }

    @media (hover: hover) and (pointer: fine) {
        &:hover {
            background-color: var(--button-secondary-state-hover);
        }
    }

    &:focus {
        background-color: var(--button-secondary-state-press);
    }

    .user-area-button {
        max-width: 182px;
        width: 100%;
        min-width: 40px;

        display: flex;
        align-items: center;

        overflow: hidden;

        .user-profile-pic {
            width: 40px;
            height: 40px;

            flex-shrink: 0;

            border-radius: 100%;
            object-fit: cover;
        }

        .user-name {
            width: 100%;
            height: 16px;
            margin: 0 16px 0 8px;

            @include H7-Medium;
            color: var(--secondary-dark-3);
            line-height: 16px;
            text-transform: capitalize;

            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        svg {
            margin-right: 16px;
            flex-shrink: 0;

            :deep(.change-color-fill) {
                fill: var(--secondary-dark-2);
            }
        }
    }

    &.mobile {
        width: 40px;
    }
}

.user-area-menu {
    width: 400px;

    display: flex;
    flex-direction: column;

    top: 8px;
    right: 8px;
    position: absolute;
    z-index: 3;

    border-radius: 8px;
    background-color: var(--secondary-light-4);
    box-shadow: 0px 6px 24px 0px rgba(0, 0, 0, 0.16);
    overflow: hidden;

    &:before {
        content: '';
        width: 100%;
        height: 128px;

        position: absolute;
        background-image: var(--gradient-vertical);
    }

    .top-area-wrapper {
        width: 100%;
        height: 112px;
        padding: 16px;

        display: flex;
        align-items: center;
        z-index: 2;

        .user-profile-pic {
            width: 80px;
            height: 80px;
            margin-right: 16px;

            flex-shrink: 0;
            border-radius: 100%;
            object-fit: cover;
        }

        .left-side-wrapper {
            width: 100%;

            display: flex;
            flex-direction: column;
            gap: 8px;

            .user-name {
                width: 100%;
                height: 28px;
                margin: 0;

                @include H3-Medium;
                color: var(--secondary-light-4);
                line-height: 28px;
                text-transform: capitalize;

                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            .button-secondary {
                width: fit-content;
                flex-grow: 0;
            }
        }
    }

    .bottom-area-wrapper {
        width: 100%;
        height: 100%;
        padding: 16px;

        display: flex;
        flex-direction: column;
        gap: 24px;

        background-color: var(--secondary-light-4);
        border-radius: 16px 16px 0px 0px;
        z-index: 1;

        .current-session-wrapper {
            width: 100%;
            height: 48px;
            padding: 0 4px 0 16px;

            display: flex;
            align-items: center;

            border-radius: 8px;
            border: 1px solid var(--secondary-light-2);

            .current-session-acronym-wrapper {
                width: 32px;
                height: 32px;
                margin-right: 8px;

                display: flex;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;

                border: 1px solid var(--secondary-light-2);
                border-radius: 6px;

                @include H7-Medium;
                color: var(--secondary-dark-2);

                background-color: var(--secondary-light-4);
            }

            .current-session {
                width: 100%;

                .acession-name {
                    height: 16px;

                    @include H7-Medium;
                    color: var(--primary);
                    line-height: 16px;

                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .account-groups-type {
                    height: 13px;

                    @include H8-Regular;
                    color: var(--secondary-dark-1);
                    line-height: 13px;
                }
            }
        }

        .buttons-bottom-wrapper {
            width: 100%;

            display: flex;
            justify-content: space-between;

            .tertiary-button-wrapper, .button-secondary {
                width: auto;
                flex-shrink: 1;
            }
        }
    }

    &.dark-mode {
        .top-area-wrapper {
            .left-side-wrapper {
                .user-name {
                    color: var(--secondary-dark-3);
                }
            }
        }
    }

    &.mobile {
        width: 100%;
        height: 100%;

        top: 0;
        right: 0;
        border-radius: 0;

        transition: transform 0.3s linear;
        transform: translateX(0);

        &.user-menu-area-animation-enter-active,
        &.user-menu-area-animation-leave-active {
            transform: translateX(0);
        }

        &.user-menu-area-animation-enter-from,
        &.user-menu-area-animation-leave-to {
            transform: translateX(110%);
        }

        &:before {
            content: '';
            width: 100%;
            height: 208px;

            position: absolute;
            background-image: var(--gradient-vertical);
        }

        .top-area-wrapper {
            height: 192px;
            flex-wrap: wrap;

            &.mobile-status-bar-android {
                height: calc(192px + var(--android-top-padding));

                padding-top: calc(var(--android-top-padding) + 16px);
            }

            &.mobile-status-bar-ios {
                height: calc(192px + constant(safe-area-inset-top));
                height: calc(192px + env(safe-area-inset-top));

                padding-top: constant(safe-area-inset-top);
                padding-top: env(safe-area-inset-top);
            }

            .mobile-only-components-wrapper {
                width: 100%;
                margin-bottom: 16px;

                display: flex;
                justify-content: space-between;
            }

            .left-side-wrapper {
                width: calc(100% - 96px);
            }
        }

        .bottom-area-wrapper {
            padding: 16px 0;

            .current-session-wrapper {
                padding-right: 16px;
                border: unset;
            }

            .buttons-bottom-wrapper {
                padding: 16px;

                flex-direction: column-reverse;
                gap: 8px;

                border-top: 1px solid var(--secondary-light-2);
            }
        }

    }
}
</style>
